// © 2025 Ananda Kumar. All rights reserved.
// Unauthorized copying of this file, via any medium, is strictly prohibited.

import { HttpClient } from '@angular/common/http';
import { Component, DestroyRef, ElementRef, HostListener, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { ApiService } from '../../../services/api.service';
import { BackgroundService } from '../../../services/background.service';
import { extractTime, formatDateArray } from '../../../utils/date-utils';
import { TenantService } from '../../../services/tenant.service';
import { EventService } from '../../../services/event.service';
import { LiveFeedCacheService } from '../../../services/live-feed-cache.service';

@Component({
  selector: 'app-venue',
  standalone: false,
  templateUrl: './venue.component.html',
  styleUrl: './venue.component.css'
})
export class VenueComponent {
  private destroyRef = inject(DestroyRef);

  @ViewChild('venueScrollContainer', { static: false }) venueScrollContainer!: ElementRef;

  eventItems: any[] = [];
  userRoles: string[] = [];
  activeTooltip: any = null;

  isLoaded = false;
  feedData: any;
  urlInput: string = '';

  constructor(
    private http: HttpClient,
    private router: Router,
    private apiService: ApiService,
    private backGroundService: BackgroundService,
    private authService: AuthService,
    public tenantService: TenantService,
    private eventService: EventService,
    private liveFeedCacheService: LiveFeedCacheService) {
  }

  ngOnInit() {
    this.userRoles = this.authService.getUserRoles();
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    this.eventService.getEvents().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((data: any) => {
        this.eventItems = data.map((item: any) => {
          return {
            ...item,
            date: formatDateArray(item.dates),
            time: item.dates.length == 1 && item.dates[0] ? extractTime(item.dates[0]) : null,
          }
        });
      });


    this.liveFeedCacheService.getLiveFeedUrl().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (data: any[]) => {
        if (data.length > 0) {
          this.isLoaded = true;
          this.feedData = data.at(0);
          this.urlInput = this.feedData?.url;
        }
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  ngAfterViewInit() {
    // Moved to ngOnInit to avoid ExpressionChangedAfterItHasBeenCheckedError
  }

  showItem(item: any) {
    if (!item.rolesRequired || item.rolesRequired.length < 1) {
      return true;
    }
    return item.rolesRequired.some((role: any) => this.userRoles.includes(role) || this.userRoles.includes('admin'));
  }

  showSplBadge(item: any): boolean {
    const requiredRoles = item.rolesRequired || [];

    return requiredRoles.includes('closefriend') &&
      (this.userRoles.includes('closefriend') || this.userRoles.includes('admin')) &&
      !requiredRoles.includes('classifiedeventviewer');
  }

  toggleTooltip(item: any, event: Event) {
    event.stopPropagation();

    if (this.activeTooltip && this.activeTooltip !== item) {
      this.activeTooltip.showTooltip = false;
    }

    item.showTooltip = !item.showTooltip;
    this.activeTooltip = item.showTooltip ? item : null;
  }

  navigateToLiveEvent() {
    this.router.navigate(['/event/liveEvent']);
  }


  scrollVenue(direction: 'left' | 'right'): void {
    const container = this.venueScrollContainer?.nativeElement;
    if (!container) return;

    const scrollAmount = 450;
    container.scrollBy({
      left: direction === 'right' ? scrollAmount : -scrollAmount,
      behavior: 'smooth'
    });
  }

  @HostListener('document:click', ['$event'])
  closeTooltip(event: Event) {
    if (this.activeTooltip) {
      this.activeTooltip.showTooltip = false;
      this.activeTooltip = null;
    }
  }
}